import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { formatCandidateShortName, pickSafeHHResumeUrl } from '../utils/candidatePrivacy';

interface HHResumeModalProps {
  open: boolean;
  onClose: () => void;
  resumeData: any;
  loading?: boolean;
  searchKeywords?: string[];
  searchCities?: string[]; // Города из фильтра поиска для подсветки
}

const HHResumeModal: React.FC<HHResumeModalProps> = ({
  open,
  onClose,
  resumeData,
  loading = false,
  searchKeywords = [],
  searchCities = []
}) => {
  const colors = useBrandingColors();
  const [resume, setResume] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const resumePhotoUrl: string | null = null;
  const resumeAlternateUrl = pickSafeHHResumeUrl(resume);
  const resumeCandidateName = resume
    ? formatCandidateShortName({
      first_name: resume.first_name,
      last_name: resume.last_name,
      middle_name: resume.middle_name,
      fullName: resume.full_name,
      name: resume.name,
    }, '')
    : '';

  const matchedKeywords = useMemo(() => {
    if (!resume) return [];
    const text = String(JSON.stringify(resume) || '');
    const kws = Array.isArray(searchKeywords) ? searchKeywords : [];
    const found: string[] = [];
    const seen = new Set<string>();
    for (const kw of kws) {
      const s = String(kw ?? '').trim();
      if (!s) continue;
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const escapedKeyword = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(^|[^а-яёА-ЯЁa-zA-Z0-9_])(${escapedKeyword})`, 'iu');
      if (regex.test(text)) found.push(s);
    }
    return found;
  }, [resume, searchKeywords]);

  useEffect(() => {
    if (open && resumeData?.id) {
      fetchResume();
    }
  }, [open, resumeData]);

  const fetchResume = async () => {
    if (!resumeData?.id) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/hh/resume/${resumeData.id}`);
      setResume(response.data);
    } catch (err: any) {
      // Check for 429 error specifically
      if (err.response?.status === 429) {
        setError('Исчерпан лимит просмотров резюме в HH.ru API. Пожалуйста, попробуйте позже.');
      } else {
        // Ensure error is always a string
        const errorMessage = typeof err.response?.data?.error === 'string' 
          ? err.response?.data?.error 
          : err.response?.data?.message || err.message || 'Ошибка загрузки резюме';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Русская плюрализация для лет/месяцев
  const pluralRu = (n: number, forms: [string, string, string]): string => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 19) return forms[2];
    if (mod10 === 1) return forms[0];
    if (mod10 >= 2 && mod10 <= 4) return forms[1];
    return forms[2];
  };

  // Длительность периода работы в формате HH ("2 года 3 месяца"). end=null → по текущий момент.
  const formatPeriodDuration = (start: string, end: string | null | undefined): string => {
    if (!start) return '';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '';
    // HH считает включительно: январь–январь = 1 месяц
    const totalMonths =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth()) + 1;
    if (totalMonths <= 0) return '';
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} ${pluralRu(years, ['год', 'года', 'лет'])}`);
    if (months > 0) parts.push(`${months} ${pluralRu(months, ['месяц', 'месяца', 'месяцев'])}`);
    return parts.join(' ');
  };

  // Проверка, соответствует ли город фильтру поиска
  const isCityMatched = (cityName: string | undefined, cityId?: string) => {
    if (!cityName && !cityId) return false;
    if (!searchCities || searchCities.length === 0) return false;

    // Проверяем по ID или по имени
    return searchCities.some(searchCity => {
      if (cityId && searchCity === cityId) return true;
      if (cityName && cityName.toLowerCase().includes(searchCity.toLowerCase())) return true;
      if (cityName && searchCity.toLowerCase().includes(cityName.toLowerCase())) return true;
      return false;
    });
  };

  // Функция для подсветки найденных ключевых слов
  const highlightText = (text: string) => {
    // Convert to string if it's an object or other type
    const textStr = typeof text === 'string' ? text : 
                    typeof text === 'object' && text !== null ? JSON.stringify(text) : 
                    String(text || '');
    
    if (!textStr || !searchKeywords || searchKeywords.length === 0) {
      return textStr;
    }

    let highlightedText = textStr;
    const keywords = Array.isArray(searchKeywords) ? searchKeywords : [searchKeywords];
    
    keywords.forEach(keyword => {
      if (keyword) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|[^а-яёА-ЯЁa-zA-Z0-9_])(${escapedKeyword})`, 'giu');
        highlightedText = highlightedText.replace(regex, '$1<mark class="bg-yellow-300">$2</mark>');
      }
    });

    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Рекурсивная функция для обхода и отображения JSON с подсветкой
  const renderJSON = (obj: any, level = 0) => {
    if (obj === null || obj === undefined) {
      return <span className="text-gray-400">null</span>;
    }

    if (typeof obj === 'string') {
      return highlightText(obj);
    }

    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return <span className="text-blue-600">{String(obj)}</span>;
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return <span className="text-gray-400">[]</span>;
      }
      return (
        <div className="ml-4">
          [
          {obj.map((item: any, index: number) => (
            <div key={index} className="ml-4">
              {renderJSON(item, level + 1)}
              {index < obj.length - 1 && ','}
            </div>
          ))}
          ]
        </div>
      );
    }

    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      if (entries.length === 0) {
        return <span className="text-gray-400">{'{}'}</span>;
      }
      return (
        <div className="ml-4">
          {'{'}
          {entries.map(([key, value], index) => (
            <div key={key} className="ml-4">
              <span className="text-purple-600">"{key}"</span>: {renderJSON(value, level + 1)}
              {index < entries.length - 1 && ','}
            </div>
          ))}
          {'}'}
        </div>
      );
    }

    return <span>{String(obj)}</span>;
  };

  // Красивое отображение резюме
  const renderResumeSection = (title: string, content: any) => {
    if (!content || (Array.isArray(content) && content.length === 0)) {
      return null;
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          {typeof content === 'string' ? (
            <p className="text-gray-700">{highlightText(content)}</p>
          ) : (
            renderJSON(content)
          )}
        </div>
      </div>
    );
  };

  if (!open) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4 text-center">Загрузка резюме...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const is429Error = error.includes('лимит просмотров');
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 max-w-md">
          <div className="flex items-start space-x-3">
            {is429Error ? (
              <svg className="h-6 w-6 text-yellow-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="h-6 w-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {is429Error ? 'Превышен лимит запросов' : 'Ошибка загрузки'}
              </h3>
              <p className="text-gray-600">{error}</p>
              {is429Error && (
                <p className="text-sm text-gray-500 mt-2">
                  Подождите несколько минут перед следующей попыткой
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="mt-6 w-full px-4 py-2 text-white rounded-3xl font-medium transition-colors"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-4">
            {resumePhotoUrl ? (
              <>
                <img
                  src={resumePhotoUrl}
                  alt="Фото"
                  className="h-16 w-16 rounded-full object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity bg-gray-200"
                  onClick={() => setIsImageZoomed(true)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden h-16 w-16 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                {isImageZoomed && (
                  <div 
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 cursor-zoom-out"
                    onClick={() => setIsImageZoomed(false)}
                  >
                    <div className="relative max-w-[90vw] max-h-[90vh]">
                      <img 
                        src={resumePhotoUrl}
                        alt="Фото (увеличено)" 
                        className="max-w-full max-h-[90vh] object-contain rounded-lg cursor-default"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button 
                        className="absolute top-[-40px] right-[-40px] text-white hover:text-gray-300 transition-colors"
                        onClick={() => setIsImageZoomed(false)}
                      >
                        <XMarkIcon className="h-10 w-10" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {resume?.title ? highlightText(resume.title) : 'Резюме'}
              </h2>
              {resumeCandidateName ? (
                <p className="text-gray-600 mt-1">
                  {resumeCandidateName}
                </p>
              ) : null}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div>
              {/* Basic Info */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Основная информация</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {resume?.gender?.name && (
                    <p><span className="font-medium">Пол:</span> {resume.gender.name}</p>
                  )}
                  {resume?.age && (
                    <p><span className="font-medium">Возраст:</span> {resume.age}</p>
                  )}
                  {resume?.area?.name && (
                    <p>
                      <span className="font-medium">Город:</span>{' '}
                      <span className={isCityMatched(resume.area.name, resume.area.id) ? 'bg-green-200 px-1 rounded' : ''}>
                        {resume.area.name}
                      </span>
                    </p>
                  )}
                  {resume?.metro?.name && (
                    <p><span className="font-medium">Метро:</span> {resume.metro.name}</p>
                  )}
                  {resume?.salary && (
                    <p><span className="font-medium">Зарплата:</span> {resume.salary.amount} {resume.salary.currency}</p>
                  )}
                  {resume?.total_experience?.months !== undefined && (
                    <p><span className="font-medium">Опыт:</span> {resume.total_experience.months > 0 ? `${Math.floor(resume.total_experience.months / 12)} лет ${resume.total_experience.months % 12 > 0 ? `${resume.total_experience.months % 12} мес` : ''}` : 'без опыта'}</p>
                  )}
                  {resume?.updated_at && (
                    <p><span className="font-medium">Обновлено:</span> {new Date(resume.updated_at).toLocaleDateString('ru-RU')}</p>
                  )}
                </div>
              </div>

              {/* Citizenship & Work Permit */}
              {(resume?.citizenship?.length > 0 || resume?.work_ticket?.length > 0) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Гражданство и разрешение на работу</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {resume?.citizenship?.length > 0 && (
                      <p><span className="font-medium">Гражданство:</span> {resume.citizenship.map((c: any) => c.name).join(', ')}</p>
                    )}
                    {resume?.work_ticket?.length > 0 && (
                      <p><span className="font-medium">Разрешение на работу:</span> {resume.work_ticket.map((w: any) => w.name).join(', ')}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Work Preferences */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Предпочтения по работе</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {(resume?.employment?.name || resume?.employments?.length > 0) && (
                    <p><span className="font-medium">Занятость:</span> {resume.employments?.map((e: any) => e.name).join(', ') || resume.employment?.name}</p>
                  )}
                  {(resume?.schedule?.name || resume?.schedules?.length > 0) && (
                    <p><span className="font-medium">График:</span> {resume.schedules?.map((s: any) => s.name).join(', ') || resume.schedule?.name}</p>
                  )}
                  {resume?.employment_form?.length > 0 && (
                    <p><span className="font-medium">Форма занятости:</span> {resume.employment_form.map((e: any) => e.name).join(', ')}</p>
                  )}
                  {resume?.work_format?.length > 0 && (
                    <p><span className="font-medium">Формат работы:</span> {resume.work_format.map((w: any) => w.name).join(', ')}</p>
                  )}
                  {resume?.travel_time?.name && (
                    <p><span className="font-medium">Время в пути:</span> {resume.travel_time.name}</p>
                  )}
                  {resume?.business_trip_readiness?.name && (
                    <p><span className="font-medium">Командировки:</span> {resume.business_trip_readiness.name}</p>
                  )}
                  {/* Релокация */}
                  {resume?.relocation && (
                    <div>
                      <span className="font-medium">Релокация:</span>{' '}
                      <span className="text-gray-600">{resume.relocation.type?.name || 'не указано'}</span>
                      {resume.relocation.area && resume.relocation.area.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {resume.relocation.area.map((area: any, idx: number) => (
                            <span
                              key={idx}
                              className={`text-sm px-2 py-0.5 rounded ${isCityMatched(area.name, area.id) ? 'bg-green-200' : 'bg-gray-200'}`}
                            >
                              {area.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Driver License & Vehicle */}
              {(resume?.driver_license_types?.length > 0 || resume?.has_vehicle) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Транспорт</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {resume?.driver_license_types?.length > 0 && (
                      <p><span className="font-medium">Водительские права:</span> {resume.driver_license_types.map((d: any) => d.id).join(', ')}</p>
                    )}
                    {resume?.has_vehicle && (
                      <p><span className="font-medium">Личный автомобиль:</span> Есть</p>
                    )}
                  </div>
                </div>
              )}

              {/* Professional Roles */}
              {resume?.professional_roles?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Профессиональные роли</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {resume.professional_roles.map((role: any, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {role.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Skill Set */}
              {resume?.skill_set && resume.skill_set.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Навыки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {resume.skill_set.map((skill: any, index: number) => {
                        const skillText = typeof skill === 'string' ? skill : skill?.name || JSON.stringify(skill);
                        return (
                          <span 
                            key={index} 
                            className="px-3 py-1 rounded-full text-sm"
                            style={{ 
                              backgroundColor: `${colors.primary}20`,
                              color: colors.primary,
                              border: `1px solid ${colors.primary}40`
                            }}
                          >
                            {highlightText(skillText)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Skills (additional field) */}
              {resume?.skills && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Дополнительные навыки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {typeof resume.skills === 'string' ? (
                      <p className="text-gray-700">{highlightText(resume.skills)}</p>
                    ) : Array.isArray(resume.skills) ? (
                      <div className="flex flex-wrap gap-2">
                        {resume.skills.map((skill: any, index: number) => (
                          <span 
                            key={index} 
                            className="px-3 py-1 rounded-full text-sm"
                            style={{ 
                              backgroundColor: `${colors.primary}20`,
                              color: colors.primary,
                              border: `1px solid ${colors.primary}40`
                            }}
                          >
                            {highlightText(typeof skill === 'object' ? skill.name : skill)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div>{renderJSON(resume.skills)}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Languages */}
              {resume?.language && resume.language.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Языки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                    {resume.language.map((lang: any, index: number) => (
                      <p key={index}>
                        <span className="font-medium">{lang.name}:</span> {lang.level?.name}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Certificates */}
              {resume?.certificate && resume.certificate.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Сертификаты</h3>
                  <div className="space-y-2">
                    {resume.certificate.map((cert: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <p className="font-medium">{cert.title}</p>
                        {cert.achieved_at && <p className="text-sm text-gray-500">{cert.achieved_at}</p>}
                        {cert.owner && <p className="text-sm text-gray-600">{cert.owner}</p>}
                        {cert.url && (
                          <a href={cert.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                            Посмотреть
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {resume?.recommendation && resume.recommendation.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Рекомендации</h3>
                  <div className="space-y-2">
                    {resume.recommendation.map((rec: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <p className="font-medium">{rec.name}</p>
                        {rec.position && <p className="text-sm text-gray-600">{rec.position}</p>}
                        {rec.organization && <p className="text-sm text-gray-600">{rec.organization}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div>
              {/* Experience */}
              {resume?.experience && resume.experience.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Опыт работы</h3>
                  <div className="space-y-4">
                    {resume.experience.map((exp: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold text-gray-900">{highlightText(exp.position || '')}</p>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>{highlightText(exp.company || '')}</span>
                          {exp.area?.name && (
                            <span className={`text-sm px-2 py-0.5 rounded ${isCityMatched(exp.area.name, exp.area.id) ? 'bg-green-200' : 'bg-gray-200'}`}>
                              {exp.area.name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {exp.start || ''} - {exp.end || 'по настоящее время'}
                          {(() => {
                            const duration = formatPeriodDuration(exp.start, exp.end);
                            return duration ? ` (${duration})` : '';
                          })()}
                        </p>
                        {exp.description && (
                          <p className="mt-2 text-gray-700">{highlightText(exp.description)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {resume?.education?.primary && resume.education.primary.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Образование</h3>
                  <div className="space-y-4">
                    {resume.education.primary.map((edu: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold text-gray-900">{highlightText(edu.name || '')}</p>
                        <p className="text-gray-600">{highlightText(edu.organization || '')}</p>
                        <p className="text-gray-600">{highlightText(edu.result || '')}</p>
                        <p className="text-sm text-gray-500">{edu.year || ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between items-center">
          <div className="text-sm text-gray-500 max-w-2xl">
            {matchedKeywords.length > 0 && (
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  <span className="font-medium">Подсвечено:</span>
                  {matchedKeywords.map((keyword, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {resumeAlternateUrl ? (
              <a
                href={resumeAlternateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-white rounded-3xl font-medium transition-colors"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primaryHover; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primary; }}
              >
                Открыть на HH.ru
              </a>
            ) : null}
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded-3xl font-medium transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HHResumeModal;
